import { addHours, isWithinInterval, parse, parseISO } from "date-fns";
import { ISessions } from "../ISessions";
import { datetime_format_year_monthnum_day } from "../../../../Common/modules/DateTime/DateTime.constants";

/**
 * Merges new session data with existing session data, ensuring the combined sessions
 * are sorted by date in ascending order based on the session keys.
 *
 * @param {ISessions.IDateWiseSessionDataList} newSessions - The new session data to be inserted.
 * @param {ISessions.IDateWiseSessionDataList} oldSessions - The existing session data (optional).
 * @returns {ISessions.IDateWiseSessionDataList} - The merged and sorted session data.
 */
export const handleInsertNewAndSortSessionsMap = (
  newSessions: ISessions.IDateWiseSessionDataList,
  oldSessions?: ISessions.IDateWiseSessionDataList,
): ISessions.IDateWiseSessionDataList => {
  // Convert existing session data into an array of [key, value] pairs, or an empty object if undefined.
  const newEntries = Object.entries(oldSessions || {});

  // Convert new session data into an array of [key, value] pairs.
  const oldEntries = Object.entries(newSessions);

  // Combine both old and new session entries into a single array.
  const entries = [...newEntries, ...oldEntries];

  // Sort the entries by their session date, using the keys as date strings.
  entries.sort(
    ([a], [b]) =>
      new Date(
        parse(a as string, datetime_format_year_monthnum_day, new Date()), // Parse and convert key 'a' to a Date object.
      ).valueOf() -
      new Date(
        parse(b as string, datetime_format_year_monthnum_day, new Date()), // Parse and convert key 'b' to a Date object.
      ).valueOf(),
  );

  // Convert the sorted array back into an object.
  const updatedSessions = Object.fromEntries(entries);

  // Return the updated sessions map.
  return updatedSessions;
};

export const isSessionOngoingOrWithinTwoHours = (
  session: ISessions.ISessionData,
) => {
  if (!session) return false;
  const currentTime = new Date();
  const startTime = parseISO(session.session_start_time);
  const endTime = parseISO(session.session_end_time);

  // Check if the session is ongoing
  const isOngoing = isWithinInterval(currentTime, {
    start: startTime,
    end: endTime,
  });

  // Check if the session starts within the next 2 hours
  const twoHoursFromNow = addHours(currentTime, 2);

  const isWithinTwoHours =
    startTime >= currentTime && startTime <= twoHoursFromNow;

  // Return true if either condition is met
  return isOngoing || isWithinTwoHours;
};
